import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import { getItem, removeItem, setItem } from "./storage";

const AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_BASE_API,
  headers: {
    "Content-Type": "application/json",
  },
});

const REFRESH_BEFORE_EXPIRY_SEC = 10 * 60;

const getTokenKeyFromRole = (Role) => {
  if (Role === "Company" || Role === "Admin") return "adminToken";
  if (Role === "Worker") return "workerToken";
  if (Role === "Customer") return "customerToken";
  if (Role === "Donor") return "donorToken";
  return "adminToken";
};

const getToken = () => {
  const customerToken = getItem("customerToken");
  if (customerToken) {
    return customerToken;
  }
  const adminToken = getItem("adminToken");
  if (adminToken) {
    return adminToken;
  }
  const workerToken = getItem("workerToken");
  if (workerToken) {
    return workerToken;
  }
  const donorToken = getItem("donorToken");
  if (donorToken) {
    return donorToken;
  }
  return null;
};

const getId = () => {
  const customerId = getItem("CustomerId");
  if (customerId) {
    return customerId;
  }
  const adminId = getItem("admin_id");
  if (adminId) {
    return adminId;
  }
  const companyId = getItem("CompanyId");
  if (companyId) {
    return companyId;
  }
  const workerId = getItem("worker_id");
  if (workerId) {
    return workerId;
  }
  const donorId = getItem("DonorId");
  if (donorId) {
    return donorId;
  }
  return null;
};

let isRefreshing = false;

const refreshTokenIfNeeded = async () => {
  const token = getToken();
  if (!token) return token;
  try {
    const decoded = jwtDecode(token);
    const exp = decoded?.exp;
    if (!exp) return token;
    const nowSec = Math.floor(Date.now() / 1000);
    const secondsLeft = exp - nowSec;
    if (secondsLeft > REFRESH_BEFORE_EXPIRY_SEC) return token;
    if (isRefreshing) return token;
    isRefreshing = true;
    const baseURL = import.meta.env.VITE_BASE_API;
    const res = await axios.post(`${baseURL}/v1/auth/refresh-token`, { token });
    const newToken = res?.data?.token;
    if (newToken && res?.data?.statusCode === 200) {
      const newDecoded = jwtDecode(newToken);
      const key = getTokenKeyFromRole(newDecoded?.Role);
      setItem(key, newToken);
      return newToken;
    }
  } catch (e) {
    // ignore
  } finally {
    isRefreshing = false;
  }
  return token;
};

AxiosInstance.interceptors.request.use(async (config) => {
  const requestUrl = config?.url || "";
  const isRefreshRequest = requestUrl.includes("/auth/refresh-token");
  let token = getToken();
  if (token && !isRefreshRequest) {
    token = await refreshTokenIfNeeded();
  }
  const id = getId();
  if (token && id) {
    config.headers["authorization"] = `CMS ${token}`;
    config.headers["id"] = `CMS ${id}`;
  } else if (token) {
    config.headers["authorization"] = `CMS ${token}`;
  }
  return config;
});

let isLoggingOut = false;

// Router navigator wired in by a top-level React component on mount via
// setAxiosNavigator(navigate). When set, doLogout uses SPA navigation. The
// custom-event fallback below covers the case where the bridge hasn't mounted
// yet (early 401 during boot) so we still avoid a full page reload.
let routerNavigator = null;
export const setAxiosNavigator = (nav) => {
  routerNavigator = nav;
};

export const FORCE_LOGOUT_EVENT = "cms:force-logout";

const doLogout = () => {
  if (isLoggingOut) return;
  isLoggingOut = true;
  toast.error("Your session has expired. Please log in again.", {
    position: "top-center",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    toastId: "session-expired-toast",
  });
  removeItem("adminToken");
  removeItem("workerToken");
  removeItem("customerToken");
  removeItem("donorToken");
  removeItem("admin_id");
  removeItem("worker_id");
  removeItem("CustomerId");
  removeItem("DonorId");
  removeItem("CompanyId");
  removeItem("CompanyUrl");
  removeItem("isImpersonated");
  removeItem("impersonatedBy");
  removeItem("oldSession");
  removeItem("oldAdminId");
  removeItem("currentLogoPath");
  localStorage.removeItem("companyName");
  if (window.clearAuthCache) window.clearAuthCache();
  if (routerNavigator) {
    routerNavigator("/auth/login", { replace: true });
  } else if (typeof window !== "undefined") {
    // Bridge not mounted yet (very-early 401 during app boot). Push history
    // state directly so React Router re-renders without a full reload, and
    // also dispatch the event in case a listener is registered.
    window.dispatchEvent(new CustomEvent(FORCE_LOGOUT_EVENT));
    if (window.location.pathname !== "/auth/login") {
      window.history.pushState({}, "", "/auth/login");
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
  }
  isLoggingOut = false;
};

AxiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isLoggingOut) return Promise.reject(error);
    const originalRequest = error.config;

    // Show user-friendly toast when upload/document size is too large (413 from nginx, or 400 from multer)
    const fileTooLargeMsg = (error.response?.data?.message || "").toString();
    const isFileTooLarge =
      error.response?.status === 413 ||
      (error.response?.status === 400 && /file size too large/i.test(fileTooLargeMsg));
    if (isFileTooLarge) {
      toast.warning("File size too large", {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        toastId: "file-too-large-toast",
      });
      return Promise.reject(error);
    }

    if (error.response?.status !== 401) return Promise.reject(error);

    const requestUrl = originalRequest?.url || error.config?.url || "";
    const requestMethod = (originalRequest?.method || error.config?.method || "").toLowerCase();
    const isLoginPost = requestMethod === "post" && requestUrl.includes("/auth/login");
    const isRefreshRequest = requestUrl.includes("/auth/refresh-token");
    const isPublicPayment =
      requestMethod === "get" && requestUrl.includes("/invoice/InvoicePayment");

    if (isLoginPost || isPublicPayment) return Promise.reject(error);

    // Suppress the "session expired" toast / forced redirect on every auth
    // page — login, signup, forgot-password, reset-password, etc. The user
    // is unauthenticated by definition there; a 401 from a public-data fetch
    // (industry dropdown, etc.) is not a session expiry.
    const currentPath = window.location.pathname;
    const isOnAuthPage = currentPath.includes("/auth/");
    if (isOnAuthPage) return Promise.reject(error);

    if (isRefreshRequest) {
      doLogout();
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      doLogout();
      return Promise.reject(error);
    }

    const token = getToken();
    if (!token) {
      doLogout();
      return Promise.reject(error);
    }

    try {
      const baseURL = import.meta.env.VITE_BASE_API;
      const res = await axios.post(`${baseURL}/v1/auth/refresh-token`, { token });
      const newToken = res?.data?.token;
      if (newToken && res?.data?.statusCode === 200) {
        const decoded = jwtDecode(newToken);
        const key = getTokenKeyFromRole(decoded?.Role);
        setItem(key, newToken);
        originalRequest.headers["authorization"] = `CMS ${newToken}`;
        originalRequest._retry = true;
        return AxiosInstance(originalRequest);
      }
    } catch (refreshErr) {
      // refresh fail
    }
    doLogout();
    return Promise.reject(error);
  }
);

// JOB-3252: GET-request dedupe. Many pages mount Sidebar + Layout + Dashboard
// in parallel, and each independently calls the same worker/graph endpoint
// (e.g. /v1/worker/get-permissions/:id was observed 3× per page, dashboard
// counts 4×). Track in-flight GETs by URL+params and let concurrent callers
// share the same promise. Released on settle.
const inFlightGets = new Map();

const dedupeKey = (url, config) => {
  if (!url) return null;
  const params = config?.params ? JSON.stringify(config.params) : "";
  return `${url}?${params}`;
};

const originalGet = AxiosInstance.get.bind(AxiosInstance);
AxiosInstance.get = function dedupedGet(url, config) {
  const key = dedupeKey(url, config);
  if (!key) return originalGet(url, config);

  const existing = inFlightGets.get(key);
  if (existing) return existing;

  const promise = originalGet(url, config).finally(() => {
    if (inFlightGets.get(key) === promise) inFlightGets.delete(key);
  });
  inFlightGets.set(key, promise);
  return promise;
};

export default AxiosInstance;
